/**
 * Sohbet listesi bileşeni
 * Kullanıcının tüm sohbetlerini listeler
 */

import { chatService, userService, generateProfilePicture, presenceMetaFromLastSeenOnly, isAdminProfile, describeGroupClose } from '../services/firebase.js';

function escapeHtmlAttr (s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
}

export default class ChatList {
  constructor(container, userId) {
    if (!container || !userId) {
      //console.error("ChatList: Container veya userId tanımlanmamış!");
      return;
    }
    
    this.container = container;
    this.userId = userId;
    this.chats = [];
    this.selectedChatId = null;
    this.unsubscribeListener = null;
    /** @type {Map<string, () => void>} */
    this._peerPresenceUnsubs = new Map();
    this.userRole = "user"; // Varsayılan rol
    this.isAdmin = false;
    
    // Kullanıcı rolünü chatDB'den çek
    this.setUserRoleFromProfile();

    document.addEventListener('chat-archived', (e) => {
      if (this.selectedChatId && this.selectedChatId === e.detail?.chatId) {
        this.selectedChatId = null;
      }
    });

    this.render();
    this.listenToChats();
  }
  
  async setUserRoleFromProfile() {
    try {
      const userProfile = await userService.getUserProfile(this.userId);
      this.userRole = userProfile?.userRole || userProfile?.role || "user";
      this.isAdmin = isAdminProfile(userProfile);
      //console.log("Kullanıcı rolü (profil):", this.userRole);
      // Duyuru grubu kontrolü kaldırıldı
      // this.checkAnnouncementGroup();
    } catch (error) {
      //console.error("Kullanıcı rolü profil üzerinden alınırken hata:", error);
    }
  }
  
  // Duyuru grubu kontrol et
  async checkAnnouncementGroup() {
    try {
      // İsmi "Duyurular" olan ve isAnnouncement bayrağına sahip olan grubu ara
      const announcementGroup = this.chats.find(chat => 
        chat.isGroup && (chat.isAnnouncement || chat.groupName === "Duyurular"));
      
      // Eğer duyuru grubu yoksa ve kullanıcı yetkili ise oluşturma seçeneği sun
      if (!announcementGroup && ["admin", "moderator", "control"].includes(this.userRole.toLowerCase())) {
        this.showCreateAnnouncementModal();
      }
    } catch (error) {
      //console.error("Duyuru grubu kontrolü sırasında hata:", error);
    }
  }
  
  // Duyuru grubu oluşturma modalı
  showCreateAnnouncementModal() {
    const modalHtml = `
      <div class="modal-content">
        <div class="modal-header">
          <h3>Duyuru Grubu Oluştur</h3>
          <button class="modal-close-btn">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <div class="modal-body">
          <p>Henüz sistemde bir duyuru grubu bulunmuyor. Duyuru grubu, tüm kullanıcıların otomatik olarak dahil olduğu ve sadece yetkili kişilerin mesaj gönderebileceği bir gruptur.</p>
          <div class="input-group">
            <label for="announcement-name" class="input-label">Duyuru Grubu Adı</label>
            <input type="text" id="announcement-name" class="input-field" value="Duyurular" placeholder="Grup adı girin...">
          </div>
          <div class="input-group">
            <label for="announcement-desc" class="input-label">Açıklama</label>
            <textarea id="announcement-desc" class="input-field" 
              placeholder="Duyuru grubu açıklaması..."
              rows="3">Bu grup duyurular için kullanılmaktadır. Sadece yöneticiler mesaj gönderebilir.</textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button id="create-announcement-btn" class="btn primary-btn">Duyuru Grubu Oluştur</button>
          <button class="btn secondary-btn modal-close-btn">İptal</button>
        </div>
      </div>
    `;
    
    // Modal oluştur
    const modal = document.createElement('div');
    modal.className = 'modal';
    modal.innerHTML = modalHtml;
    document.body.appendChild(modal);
    
    // Modal kapatma işlevi
    const closeModal = () => {
      document.body.removeChild(modal);
    };
    
    // Kapatma düğmelerini dinle
    modal.querySelectorAll('.modal-close-btn').forEach(btn => {
      btn.addEventListener('click', closeModal);
    });
    
    // Dışa tıklama ile kapatma
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        closeModal();
      }
    });
    
    // Duyuru grubu oluşturma butonu
    const createBtn = modal.querySelector('#create-announcement-btn');
    createBtn.addEventListener('click', async () => {
      const groupName = modal.querySelector('#announcement-name').value.trim() || "Duyurular";
      const description = modal.querySelector('#announcement-desc').value.trim();
      try {
        // Duyuru grubu oluştur
        const chatData = {
          users: [], // Kullanıcı eklenmiyor, backend veya dinamik olarak tüm kullanıcılar dahil kabul edilir
          isGroup: true,
          groupName: groupName,
          description: description,
          isAnnouncement: true, // Özel bayrak ile duyuru grubu
          createdBy: this.userId,
          createdAt: new Date().toISOString(),
          writeAccess: ["admin", "moderator", "control"] // Sadece yetkililer yazabilir
        };
        const chatId = await chatService.createChat(chatData);
        closeModal();
        alert("Duyuru grubu başarıyla oluşturuldu!");
        if (chatId) {
          this.selectChat(chatId);
        }
      } catch (error) {
        //console.error("Duyuru grubu oluşturma hatası:", error);
        alert("Duyuru grubu oluşturulurken bir hata oluştu.");
      }
    });
  }
  
  render() {
    // Duyuru grubu stillerini ekle
    this.addAnnouncementStyles();
    
    
    // Liste boşsa "sohbet yok" mesajı göster
    if (this.chats.length === 0) {
      this.container.innerHTML = `
        <div class="empty-chats">
          <div class="empty-message">Henüz sohbet bulunmuyor.</div>
          <button id="new-chat-btn" class="new-chat-button">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Yeni Sohbet Başlat
          </button>
        </div>
      `;
    } else {
      // Duyuru gruplarını diğer gruplardan ayır
      const announcementChats = this.chats.filter(chat => chat.isAnnouncement);
      
      // Normal sohbetleri iki gruba ayır: özel ve grup sohbetleri
      const normalChats = this.chats.filter(chat => !chat.isAnnouncement);
      const privateChats = normalChats.filter(chat => !chat.isGroup);
      const groupChats = normalChats.filter(chat => chat.isGroup);
      
      this.container.innerHTML = `
        <div class="chat-list-header">
          <h3>Sohbetler</h3>
          <button id="new-chat-btn" class="icon-button" title="Yeni Sohbet">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
          </button>
        </div>
        <div class="chat-categories">
          <!-- Duyuru Grupları -->
          ${announcementChats.length > 0 ? `
          <div class="chat-category">
            <div class="category-header" data-category="announcement">
              <h4>Duyurular</h4>
              <span class="category-count">${announcementChats.length}</span>
            </div>
            <div class="chat-items announcement-chats">
              ${announcementChats.map(chat => this.renderChatItem(chat)).join('')}
            </div>
          </div>
          ` : ''}
          
          <!-- Özel Sohbetler -->
          <div class="chat-category">
            <div class="category-header" data-category="private">
              <h4>Özel Sohbetler</h4>
              <span class="category-count">${privateChats.length}</span>
            </div>
            <div class="chat-items private-chats">
              ${privateChats.length > 0 
                ? privateChats.map(chat => this.renderChatItem(chat)).join('') 
                : '<div class="empty-category">Henüz özel sohbet bulunmuyor.</div>'}
            </div>
          </div>
          
          <!-- Grup Sohbetleri -->
          <div class="chat-category">
            <div class="category-header" data-category="group">
              <h4>Grup Sohbetleri</h4>
              <span class="category-count">${groupChats.length}</span>
            </div>
            <div class="chat-items group-chats">
              ${groupChats.length > 0 
                ? groupChats.map(chat => this.renderChatItem(chat)).join('') 
                : '<div class="empty-category">Henüz grup sohbeti bulunmuyor.</div>'}
            </div>
          </div>
        </div>
      `;
    }
    
    // Yeni sohbet butonu için event listener
    const newChatBtn = this.container.querySelector('#new-chat-btn');
    if (newChatBtn) {
      newChatBtn.addEventListener('click', () => this.showNewChatModal());
    }
    
    // Sohbet öğeleri için event listener
    const chatItems = this.container.querySelectorAll('.chat-item');
    chatItems.forEach(item => {
      item.addEventListener('click', () => {
        const chatId = item.getAttribute('data-id');
        this.selectChat(chatId);
      });
      
      // Sağ tıklama menüsü
      item.addEventListener('contextmenu', (e) => {
        e.preventDefault();
        const chatId = item.getAttribute('data-id');
        const isAnnouncement = item.getAttribute('data-is-announcement') === 'true';
        const isGroup = item.getAttribute('data-is-group') === 'true';
        
        // Duyuru ve grup sohbetleri: admin grubu tamamen kapatabilir
        if (isAnnouncement || isGroup) {
          if (this.isAdmin) {
            const chat = this.chats.find((itemChat) => itemChat.id === chatId);
            this.showCloseGroupMenu(e.clientX, e.clientY, chatId, chat?.groupName || 'Grup Sohbeti');
          }
          return;
        }
        
        this.showContextMenu(e.clientX, e.clientY, chatId);
      });
    });
    
    // Kategori başlıkları için event listener
    const categoryHeaders = this.container.querySelectorAll('.category-header');
    categoryHeaders.forEach(header => {
      header.addEventListener('click', () => {
        const category = header.closest('.chat-category');
        const items = category.querySelector('.chat-items');
        
        // Toggle (genişlet/daralt) sohbet listesini
        if (items.style.display === 'none') {
          items.style.display = 'block';
          header.classList.remove('collapsed');
        } else {
          items.style.display = 'none';
          header.classList.add('collapsed');
        }
      });
    });
    
    // CSS için ek stillemeler
    if (!document.querySelector('style#chat-list-styles')) {
      const style = document.createElement('style');
      style.id = 'chat-list-styles';
      style.textContent = `
        .chat-list-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: var(--space-sm) var(--space-md);
          border-bottom: 1px solid var(--bg-tertiary);
        }
        
        .chat-list-header h3 {
          font-size: var(--font-md);
          font-weight: 600;
          color: var(--text-primary);
          margin: 0;
        }
        
        .chat-categories {
          overflow-y: auto;
          max-height: calc(100% - 50px);
        }
        
        .chat-category {
          margin-bottom: var(--space-xs);
        }
        
        .category-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: var(--space-xs) var(--space-md);
          background-color: var(--bg-secondary);
          cursor: pointer;
          border-radius: var(--radius-sm);
          transition: background-color 0.2s ease;
        }
        
        .category-header:hover {
          background-color: var(--bg-hover);
        }
        
        .category-header.collapsed {
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
        }
        
        .category-header h4 {
          font-size: var(--font-sm);
          font-weight: 500;
          color: var(--text-secondary);
          margin: 0;
        }
        
        .category-count {
          background-color: var(--bg-tertiary);
          border-radius: 20px;
          padding: 2px 8px;
          font-size: var(--font-xs);
          color: var(--text-secondary);
        }
        
        .empty-category {
          padding: var(--space-md);
          text-align: center;
          color: var(--text-tertiary);
          font-size: var(--font-sm);
        }
        
        .icon-button {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--text-secondary);
          transition: var(--transition-fast);
        }
        
        .icon-button:hover {
          background-color: var(--bg-hover);
          color: var(--text-primary);
        }
        
        .empty-chats {
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: var(--space-xl);
          text-align: center;
        }
        
        .empty-message {
          color: var(--text-secondary);
          margin-bottom: var(--space-lg);
        }
        
        .new-chat-button {
          display: flex;
          align-items: center;
          gap: var(--space-xs);
          padding: var(--space-sm) var(--space-md);
          background-color: var(--bg-accent);
          color: white;
          border-radius: var(--radius-md);
          font-weight: 500;
          transition: var(--transition-fast);
        }
        
        .new-chat-button:hover {
          opacity: 0.9;
        }
        
        .chat-items {
          overflow-y: auto;
        }
        
        .unread-indicator {
          display: inline-block;
          width: 8px;
          height: 8px;
          background-color: var(--bg-accent);
          border-radius: 50%;
          margin-right: var(--space-xs);
        }

        .chat-name-container {
          display: flex;
          align-items: center;
          gap: var(--space-xs);
        }

        .chat-user-role {
          font-size: var(--font-xs);
          background-color: var(--bg-tertiary);
          color: var(--text-secondary);
          padding: 2px 6px;
          border-radius: var(--radius-sm);
          font-weight: 500;
          white-space: nowrap;
        }
        
        /* Okunmamış mesajı olan sohbetler için mor görünüm */
        .chat-item {
          background-color: transparent;
        }
        
        .chat-item.unread-chat {
          background-color: rgba(106, 27, 154, 0.08);
        }
        
        .chat-item.unread-chat .chat-name {
          font-weight: 600;
          color: #6a1b9a;
        }
        
        .chat-badge {
          background-color: #6a1b9a;
          color: white;
          border-radius: 50%;
          width: 18px;
          height: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          font-weight: 600;
        }

        .chat-avatar.chat-avatar-private-wrap {
          position: relative;
          overflow: visible;
          background: transparent;
        }
        .chat-avatar-private-inner {
          width: 100%;
          height: 100%;
          border-radius: 50%;
          overflow: hidden;
          background-color: var(--bg-tertiary);
        }
        .chat-avatar-private-inner img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        .private-chat-presence-dot {
          position: absolute;
          bottom: 0;
          right: 0;
          width: 11px;
          height: 11px;
          border-radius: 50%;
          border: 2px solid var(--bg-primary, #121212);
          box-sizing: border-box;
          pointer-events: none;
        }
        .private-chat-presence-dot.presence-dot-green {
          background: #4caf50;
        }
        .private-chat-presence-dot.presence-dot-red {
          background: #f44336;
        }
        .private-chat-presence-dot.presence-dot-gray {
          background: #9e9e9e;
        }
      `;
      document.head.appendChild(style);
    }
  }
  
  renderChatItem(chat) {
    const isSelected = chat.id === this.selectedChatId;
    
    // Okunmamış mesaj kontrolü
    const hasUnread = (chat.unreadCount && chat.unreadCount > 0) ? true : false;
    
    // Sohbet adını ve profil resmini belirle
    let chatName = '';
    let photoURL = '';
    let userRole = '';
    
    if (chat.isGroup) {
      // Grup sohbeti
      chatName = chat.groupName || 'Grup Sohbeti';
      photoURL = chat.groupPhotoURL || '';
    } else {
      const users = Array.isArray(chat.users) ? chat.users : []
      const otherUserId = users.find((uid) => uid !== this.userId)
      if (!otherUserId) {
        chatName = chat.name || 'Özel sohbet'
        photoURL = chat.photoURL || ''
      } else if (chat.userDetails && chat.userDetails[otherUserId]) {
        const userDetail = chat.userDetails[otherUserId];
        const userCode = userDetail.userCode || '';
        userRole = userDetail.userRole || '';
        if (userCode && userRole) {
          chatName = `${userCode} (${userRole})`;
        } else if (userCode) {
          chatName = userCode;
        } else if (userDetail.displayName && userDetail.displayName !== 'Kullanıcı') {
          chatName = userDetail.displayName;
        } else {
          chatName = otherUserId.substring(0, 6);
        }
        photoURL = userDetail.photoURL || '';
      } else {
        chatName = chat.name || otherUserId.substring(0, 6);
        photoURL = chat.photoURL || '';
      }
    }
    
    // Profil resmi yoksa, isimden oluştur
    if (!photoURL) {
      photoURL = generateProfilePicture(chatName);
    }

    let presenceHtml = ''
    if (!chat.isGroup) {
      const users = Array.isArray(chat.users) ? chat.users : []
      const oid = users.find((uid) => uid !== this.userId)
      const od = oid && chat.userDetails ? chat.userDetails[oid] : null
      const pr = presenceMetaFromLastSeenOnly(od || {})
      presenceHtml = `<span class="private-chat-presence-dot ${pr.listDotClass}" title="${escapeHtmlAttr(pr.label)}"></span>`
    }
    
    // Son mesaj zamanı
    let timeDisplay = '';
    if (chat.lastMessageTimestamp) {
      const date = chat.lastMessageTimestamp.toDate ? chat.lastMessageTimestamp.toDate() : new Date(chat.lastMessageTimestamp);
      const now = new Date();
      const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) {
        // Bugün - sadece saat
        timeDisplay = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      } else if (diffDays === 1) {
        // Dün
        timeDisplay = 'Dün';
      } else if (diffDays < 7) {
        // Gün adı
        const days = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
        timeDisplay = days[date.getDay()];
      } else {
        // Tarih
        timeDisplay = date.toLocaleDateString();
      }
    }

    // Son mesaj içeriğini düzgün şekilde formatlama
    let lastMessageText = 'Yeni sohbet başlatıldı';
    if (chat.lastMessage) {
      if (typeof chat.lastMessage === 'string') {
        // Eğer string ise doğrudan kullan
        lastMessageText = chat.lastMessage;
      } else if (typeof chat.lastMessage === 'object') {
        // Eğer object ise content'i çıkar
        lastMessageText = chat.lastMessage.content || 'Mesaj gönderildi';
        
        // Farklı mesaj tipleri için uygun içeriği göster
        if (chat.lastMessage.type === 'image') {
          lastMessageText = '📷 Resim gönderildi';
        } else if (chat.lastMessage.type === 'video') {
          lastMessageText = '🎥 Video gönderildi';
        } else if (chat.lastMessage.type === 'document') {
          lastMessageText = '📄 Dosya gönderildi';
        }
      }
    }
    
    // Fazla uzun mesajları kısalt
    if (lastMessageText.length > 40) {
      lastMessageText = lastMessageText.substring(0, 37) + '...';
    }
    
    // Sadece okunmamış mesaj varsa unread-chat sınıfını ekle
    const unreadClass = hasUnread ? 'unread-chat' : '';
    
    // Duyuru grubu ise özel sınıf ekle
    const announcementClass = chat.isAnnouncement ? 'announcement-chat' : '';
    
    // Duyuru grubu ikonu
    const announcementIcon = chat.isAnnouncement ? 
      `<span class="announcement-icon" title="Duyuru Grubu">📢</span>` : '';
    
    return `
      <div class="chat-item ${isSelected ? 'active' : ''} ${unreadClass} ${announcementClass}" data-id="${chat.id}" data-is-announcement="${chat.isAnnouncement ? 'true' : 'false'}" data-is-group="${chat.isGroup ? 'true' : 'false'}">
        <div class="chat-avatar ${!chat.isGroup ? 'chat-avatar-private-wrap' : ''}">
          ${chat.isGroup 
            ? `<div class="group-avatar">${chatName.charAt(0)}</div>` 
            : `<div class="chat-avatar-private-inner"><img src="${photoURL}" alt="${chatName}"></div>${presenceHtml}`
          }
        </div>
        <div class="chat-info">
          <div class="chat-name-container">
            <div class="chat-name">
              ${hasUnread ? '<span class="unread-indicator"></span>' : ''}
              ${chatName} ${announcementIcon}
            </div>
            ${userRole ? `<div class="chat-user-role">${userRole}</div>` : ''}
          </div>
          <div class="chat-last-message">${lastMessageText}</div>
        </div>
        <div class="chat-meta">
          <div class="chat-time">${timeDisplay}</div>
          ${hasUnread ? `<div class="chat-badge">${chat.unreadCount}</div>` : ''}
        </div>
      </div>
    `;
  }
  
  selectChat(chatId) {
    // Önceki seçili sohbeti al
    const prevChatId = this.selectedChatId;
    
    // Aynı sohbete tekrar tıklandıysa bir şey yapma
    if (prevChatId === chatId) return;
    
    // Yeni sohbet seç
    this.selectedChatId = chatId;
    
    // Önceki aktif sohbeti bul ve aktifliği kaldır
    if (prevChatId) {
      const prevItem = this.container.querySelector(`.chat-item[data-id="${prevChatId}"]`);
      if (prevItem) {
        prevItem.classList.remove('active');
      }
    }
    
    // Yeni seçilen sohbeti bul ve aktif yap (yeni oluşturulan sohbet için önce render gerekebilir)
    let selectedItem = this.container.querySelector(`.chat-item[data-id="${chatId}"]`);
    if (!selectedItem) {
      this.render();
      selectedItem = this.container.querySelector(`.chat-item[data-id="${chatId}"]`);
    }
    if (selectedItem) {
      selectedItem.classList.add('active');

      selectedItem.classList.remove('unread-chat');
      const badge = selectedItem.querySelector('.chat-badge');
      if (badge) {
        badge.remove();
      }
      const indicator = selectedItem.querySelector('.unread-indicator');
      if (indicator) {
        indicator.remove();
      }

      const selectedChatIndex = this.chats.findIndex(c => c.id === chatId);
      if (selectedChatIndex !== -1) {
        if (this.chats[selectedChatIndex]._isNewMessage) {
          this.chats[selectedChatIndex]._isNewMessage = false;
        }
        this.chats[selectedChatIndex].unreadCount = 0;
      }
    }

    // Seçilen chat duyuru grubu mu kontrol et (liste satırı yoksa özel sohbet varsayımı)
    const isAnnouncement = selectedItem
      ? selectedItem.getAttribute('data-is-announcement') === 'true'
      : false;
    
    // Seçilen sohbeti bildir (ana uygulama dinliyor)
    const event = new CustomEvent('chat-selected', {
      detail: { 
        chatId, 
        prevChatId, 
        isAnnouncement, 
        canWrite: !isAnnouncement || ["admin", "moderator"].includes(this.userRole) 
      }
    });
    document.dispatchEvent(event);
  }
  
  // CSS stillerini ekle
  addAnnouncementStyles() {
    if (!document.querySelector('style#announcement-styles')) {
      const style = document.createElement('style');
      style.id = 'announcement-styles';
      style.textContent = `
        .announcement-chat {
          background-color: rgba(66, 135, 245, 0.05);
          border-left: 3px solid #4287f5;
        }
        
        .announcement-icon {
          font-size: 14px;
          margin-left: 4px;
          color: #4287f5;
        }
        
        .announcement-badge {
          background-color: #4287f5;
          color: white;
          font-size: 10px;
          padding: 2px 6px;
          border-radius: 10px;
          margin-left: 6px;
          font-weight: 500;
        }
      `;
      document.head.appendChild(style);
    }
  }
  
  clearPeerPresenceListeners () {
    for (const unsub of this._peerPresenceUnsubs.values()) {
      unsub()
    }
    this._peerPresenceUnsubs.clear()
  }

  /**
   * Özel sohbetlerde karşı tarafın lastSeenAt bilgisi chat belgesinde gecikmeli kalır;
   * users/{uid} anlık güncellenir — liste noktası için burayı dinleriz.
   */
  ensurePeerPresenceListeners (chats) {
    // OPTIMIZASYON: Tüm özel sohbetler için değil, yalnızca en güncel N sohbet için
    // canlı presence dinle. Liste recency'e göre sıralı olduğundan alttaki (çoğunlukla
    // ekranda görünmeyen) sohbetler son bilinen durumu (chat.userDetails) göstermeye
    // devam eder — nokta kaybolmaz, sadece daha seyrek güncellenir. Bu, presence
    // okumalarını (100 kullanıcı × onlarca sohbet ölçeğinde en büyük maliyet) ciddi azaltır.
    const MAX_PEER_PRESENCE_LISTENERS = 20
    const needed = new Set()
    for (const chat of chats) {
      if (needed.size >= MAX_PEER_PRESENCE_LISTENERS) {
        break
      }
      if (chat.isGroup) {
        continue
      }
      const users = Array.isArray(chat.users) ? chat.users : []
      const other = users.find((id) => id !== this.userId)
      if (other) {
        needed.add(other)
      }
    }
    for (const [uid, unsub] of this._peerPresenceUnsubs.entries()) {
      if (!needed.has(uid)) {
        unsub()
        this._peerPresenceUnsubs.delete(uid)
      }
    }
    for (const uid of needed) {
      if (this._peerPresenceUnsubs.has(uid)) {
        continue
      }
      const unsub = chatService.subscribeChatUserLastSeen(uid, (data) => {
        this.applyUserLastSeenFromUsersDoc(uid, data)
      })
      this._peerPresenceUnsubs.set(uid, unsub)
    }
  }

  applyUserLastSeenFromUsersDoc (uid, userData) {
    if (!userData) {
      return
    }
    const lastSeenAt = userData.lastSeenAt != null ? userData.lastSeenAt : null
    for (const chat of this.chats) {
      if (chat.isGroup) {
        continue
      }
      const users = Array.isArray(chat.users) ? chat.users : []
      const other = users.find((id) => id !== this.userId)
      if (other !== uid) {
        continue
      }
      if (!chat.userDetails) {
        chat.userDetails = {}
      }
      if (!chat.userDetails[uid]) {
        chat.userDetails[uid] = {}
      }
      chat.userDetails[uid] = { ...chat.userDetails[uid], lastSeenAt }
      const item = this.container.querySelector(`.chat-item[data-id="${chat.id}"] .private-chat-presence-dot`)
      if (item) {
        const pr = presenceMetaFromLastSeenOnly(chat.userDetails[uid] || {})
        item.className = `private-chat-presence-dot ${pr.listDotClass}`
        item.title = pr.label
      }
    }
  }

  listenToChats() {
    // Önceki dinleyici varsa kaldır
    if (this.unsubscribeListener) {
      this.unsubscribeListener();
    }
    this.clearPeerPresenceListeners()
    
    // Sohbetleri dinlemeye başla
    this.unsubscribeListener = chatService.listenToChats(this.userId, (chats) => {
      // Yeni bir mesaj geldiğinde sohbetin son mesaj zamanına ve okunma durumuna göre kontrol et
      const previousChats = [...this.chats];
      const newChats = [...chats];
      
      // Sohbetleri son mesaj tarihine göre manuel olarak sırala
      newChats.sort((a, b) => {
        // Eğer lastMessageTimestamp yoksa, o sohbet en alta gider
        if (!a.lastMessageTimestamp) return 1;
        if (!b.lastMessageTimestamp) return -1;
        
        // Firebase Timestamp objesi ise .seconds kullan, normal Date ise doğrudan karşılaştır
        const timeA = a.lastMessageTimestamp.seconds ? 
          a.lastMessageTimestamp.seconds : 
          (a.lastMessageTimestamp instanceof Date ? a.lastMessageTimestamp.getTime() / 1000 : 0);
          
        const timeB = b.lastMessageTimestamp.seconds ? 
          b.lastMessageTimestamp.seconds : 
          (b.lastMessageTimestamp instanceof Date ? b.lastMessageTimestamp.getTime() / 1000 : 0);
        
        // En büyük timestamp (en yeni) en üstte olacak şekilde sırala
        return timeB - timeA;
      });
      
      // Önceki sohbet listesinin sırasını kontrol et
      const chatOrderChanged = this.hasChatOrderChanged(previousChats, newChats);
      
      // Sıralanmış yeni chat listesini kaydet
      this.chats = newChats;
      
      // Değişiklikler için UI güncellemesi gerekip gerekmediğini kontrol et
      let needFullRender = false;
      let changedChats = [];
      
      // Her yeni gelen sohbet için değişiklik kontrolü yap
      newChats.forEach(newChat => {
        // Eğer şu anda seçili olan sohbetse, unreadCount sıfırla
        if (newChat.id === this.selectedChatId) {
          newChat.unreadCount = 0;
        }
        
        // Bu sohbetin önceki halini bul
        const prevChat = previousChats.find(pc => pc.id === newChat.id);
        
        // Sohbet yoksa, yeni eklendiyse veya son mesaj değiştiyse
        if (!prevChat) {
          // Yeni sohbet - tam render gerektirir
          needFullRender = true;
        } else {
          // Mevcut sohbet - sadece değişiklik varsa güncelle
          const lastMsgChanged = prevChat.lastMessageTimestamp?.seconds !== newChat.lastMessageTimestamp?.seconds;
          const unreadChanged = prevChat.unreadCount !== newChat.unreadCount;
          
          // Önemli bir değişiklik varsa, bu sohbeti değişenler listesine ekle
          if (lastMsgChanged || unreadChanged) {
            // Son mesaj değiştiyse ve kullanıcının kendisi göndermediyse
            if (lastMsgChanged && newChat.lastMessage?.senderId !== this.userId) {
              // Ses ile bildirim çal
              this.playNotificationSound();
              
              // Bildirim göster
              this.showNotification(newChat);
              
              // Seçili sohbet için bildirim gösterme
              if (newChat.id !== this.selectedChatId) {
                // Okunmamış mesaj sayısını artır (eğer API sağlamazsa)
                if (typeof newChat.unreadCount === "undefined") {
                  newChat.unreadCount = (prevChat && prevChat.unreadCount) ? prevChat.unreadCount + 1 : 1;
                }
              }
            }
            
            changedChats.push(newChat);
          }
        }
      });
      
      // Yeni sohbet eklendiyse, sohbet silindiyse veya sıralama değiştiyse
      if (previousChats.length !== newChats.length || chatOrderChanged) {
        needFullRender = true;
      }
      
      // Tüm UI güncellemesi gerekiyorsa
      if (needFullRender) {
        this.render();
      } else {
        // Sadece değişen sohbetleri güncelle
        changedChats.forEach(chat => {
          this.updateChatItem(chat);
        });
      }
      
      // İlk sohbeti otomatik seç (Uygulama ilk açıldığında)
      if (chats.length > 0 && !this.selectedChatId) {
        this.selectChat(chats[0].id);
      }

      this.scheduleAutoArchiveStalePrivateChats(newChats);
      this.ensurePeerPresenceListeners(this.chats);
    });
  }

  /**
   * 90 gündür mesaj yoksa özel sohbeti otomatik arşivle (en fazla günde bir kontrol).
   */
  scheduleAutoArchiveStalePrivateChats (chats) {
    const key = `privateStaleAutoArchiveAt_${this.userId}`;
    const last = parseInt(localStorage.getItem(key) || '0', 10);
    const cooldownMs = 24 * 60 * 60 * 1000;
    if (Date.now() - last < cooldownMs) return;
    if (this._autoArchiveStaleInProgress) return;
    this._autoArchiveStaleInProgress = true;
    (async () => {
      try {
        await chatService.autoArchiveStalePrivateChatsForUser(this.userId, chats, 90);
      } catch (err) {
        console.warn('Otomatik arşiv (özel, 90 gün):', err);
      } finally {
        this._autoArchiveStaleInProgress = false;
        localStorage.setItem(key, String(Date.now()));
      }
    })();
  }
  
  // Sohbet sıralamasının değişip değişmediğini kontrol et
  hasChatOrderChanged(oldChats, newChats) {
    // Aynı ID'lere sahip sohbetlerin sırasının değişip değişmediğini kontrol et
    const oldIds = oldChats.map(chat => chat.id);
    const newIds = newChats.map(chat => chat.id);
    
    // Eğer sohbet sayısı farklıysa, direkt true döndür
    if (oldIds.length !== newIds.length) return true;
    
    // ID'lerin sırasını karşılaştır
    for (let i = 0; i < oldIds.length; i++) {
      if (oldIds[i] !== newIds[i]) {
        return true;
      }
    }
    
    return false;
  }
  
  // Sadece değişen bir sohbet öğesini güncelle
  updateChatItem(chat) {
    const chatItem = this.container.querySelector(`.chat-item[data-id="${chat.id}"]`);
    if (!chatItem) return;
    
    // Sohbet aktif ise, okunmamış mesaj sayısını sıfırla
    if (chat.id === this.selectedChatId) {
      chat.unreadCount = 0;
    }
    
    // Okunmamış mesaj durumunu güncelle
    const hasUnread = (chat.unreadCount && chat.unreadCount > 0) ? true : false;
    
    if (hasUnread) {
      chatItem.classList.add('unread-chat');
      
      // Okunmamış göstergeyi ekle
      const chatName = chatItem.querySelector('.chat-name');
      if (chatName && !chatName.querySelector('.unread-indicator')) {
        const indicator = document.createElement('span');
        indicator.className = 'unread-indicator';
        chatName.insertBefore(indicator, chatName.firstChild);
      }
      
      // Okunmamış sayısı rozetini güncelle
      let badge = chatItem.querySelector('.chat-badge');
      if (!badge) {
        badge = document.createElement('div');
        badge.className = 'chat-badge';
        chatItem.querySelector('.chat-meta').appendChild(badge);
      }
      badge.textContent = chat.unreadCount;
    } else {
      chatItem.classList.remove('unread-chat');
      
      // Okunmamış göstergeyi kaldır
      const indicator = chatItem.querySelector('.unread-indicator');
      if (indicator) {
        indicator.remove();
      }
      
      // Rozeti kaldır
      const badge = chatItem.querySelector('.chat-badge');
      if (badge) {
        badge.remove();
      }
    }
    
    // Son mesaj içeriğini güncelle
    let lastMessageText = 'Yeni sohbet başlatıldı';
    if (chat.lastMessage) {
      if (typeof chat.lastMessage === 'string') {
        lastMessageText = chat.lastMessage;
      } else if (typeof chat.lastMessage === 'object') {
        lastMessageText = chat.lastMessage.content || 'Mesaj gönderildi';
        
        if (chat.lastMessage.type === 'image') {
          lastMessageText = '📷 Resim gönderildi';
        } else if (chat.lastMessage.type === 'video') {
          lastMessageText = '🎥 Video gönderildi';
        } else if (chat.lastMessage.type === 'document') {
          lastMessageText = '📄 Dosya gönderildi';
        }
      }
    }
    
    // Fazla uzun mesajları kısalt
    if (lastMessageText.length > 40) {
      lastMessageText = lastMessageText.substring(0, 37) + '...';
    }
    
    // Son mesajı güncelle
    const lastMessageEl = chatItem.querySelector('.chat-last-message');
    if (lastMessageEl) {
      lastMessageEl.textContent = lastMessageText;
    }
    
    // Son mesaj zamanını güncelle
    if (chat.lastMessageTimestamp) {
      const timeEl = chatItem.querySelector('.chat-time');
      if (timeEl) {
        const date = chat.lastMessageTimestamp.toDate ? chat.lastMessageTimestamp.toDate() : new Date(chat.lastMessageTimestamp);
        const now = new Date();
        const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
        
        let timeDisplay = '';
        if (diffDays === 0) {
          // Bugün - sadece saat
          timeDisplay = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } else if (diffDays === 1) {
          // Dün
          timeDisplay = 'Dün';
        } else if (diffDays < 7) {
          // Gün adı
          const days = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
          timeDisplay = days[date.getDay()];
        } else {
          // Tarih
          timeDisplay = date.toLocaleDateString();
        }
        
        timeEl.textContent = timeDisplay;
      }
    }
  }
  
  playNotificationSound() {
    // Bildirim sesi çal
    try {
      const notificationSound = document.getElementById('notification-sound');
      if (notificationSound) {
        notificationSound.currentTime = 0; // Her seferinde baştan çal
        notificationSound.volume = 0.5;
        notificationSound.play().catch(error => {
          // Tarayıcı autoplay politikası nedeniyle hata oluşabilir
          //console.log("Ses çalınamadı, kullanıcı etkileşimi gerekiyor olabilir");
        });
      } else {
        // Element bulunamadıysa, Audio API ile dene
        const audio = new Audio('/assets/sounds/notification.mp3');
        audio.volume = 0.5;
        audio.play().catch(error => {
          //console.log("Ses çalınamadı: ", error);
        });
      }
    } catch (error) {
      //console.error("Bildirim sesi çalınamadı:", error);
    }
  }
  
  showNotification(chat) {
    // Tarayıcı bildirimlerine izin kontrolü
    if (Notification.permission !== 'granted') {
      return;
    }
    
    // Sohbet adını ve mesaj içeriğini hazırla
    let chatName = 'Yeni Mesaj';
    let messageText = '';
    
    if (chat.isGroup) {
      chatName = chat.groupName || 'Grup Sohbeti';
    } else {
      // Özel sohbet için diğer kullanıcının KULLANICI KODUNU bul
      const otherUserId = chat.users.find(uid => uid !== this.userId);
      if (chat.userDetails && chat.userDetails[otherUserId]) {
        // Önce userCode'u kullan, yoksa displayName
        chatName = chat.userDetails[otherUserId].userCode || chat.userDetails[otherUserId].displayName || 'Kullanıcı';
      }
    }
    
    if (chat.lastMessage) {
      if (typeof chat.lastMessage === 'string') {
        messageText = chat.lastMessage;
      } else {
        if (chat.lastMessage.type === 'text') {
          messageText = chat.lastMessage.content || '';
        } else if (chat.lastMessage.type === 'image') {
          messageText = '📷 Resim gönderildi';
        } else if (chat.lastMessage.type === 'video') {
          messageText = '🎥 Video gönderildi';
        } else if (chat.lastMessage.type === 'document') {
          messageText = '📄 Dosya gönderildi';
        } else {
          messageText = 'Yeni mesaj';
        }
      }
    }
    
    // Bildirim oluştur - Kullanıcı kodu formatında
    const notificationTitle = chat.isGroup ? chatName : `${chatName}: ${messageText}`;
    const notificationBody = chat.isGroup ? messageText : '';
    
    const notification = new Notification(notificationTitle, {
      body: notificationBody,
      icon: '/chat-icon.svg'
    });
    
    // Bildirime tıklandığında sohbeti aç
    notification.onclick = () => {
      window.focus();
      this.selectChat(chat.id);
    };
  }
  
  async showNewChatModal() {
    let users = [];
    let selectedGroupUserIds = new Set();

    const userRoleLower = (this.userRole || "").toLowerCase();
    const isAdmin = userRoleLower === "admin";
    const isControl = userRoleLower === "control";
    const canCreateAnnouncement = isAdmin || isControl;
    const canCreateGroup = isAdmin || isControl;

    // Modal HTML oluştur
    const modalHtml = `
      <div class="modal-content">
        <div class="modal-header">
          <h3>Yeni Sohbet</h3>
          <button class="modal-close-btn">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <div class="modal-body">
          <div class="chat-type-selector">
            <button class="chat-type-btn active" data-type="private">Özel Sohbet</button>
            ${canCreateGroup ? `<button class="chat-type-btn" data-type="group">Grup Sohbeti</button>` : `<button class="chat-type-btn disabled" title="Bu özelliği kullanma yetkiniz yok" disabled>Grup Sohbeti</button>`}
            ${canCreateAnnouncement ? `<button class="chat-type-btn" data-type="announcement">Duyuru Grubu</button>` : ''}
          </div>
          <div id="private-chat-form" class="chat-form">
            <div class="input-group">
              <label for="user-search" class="input-label">Kullanıcı Ara</label>
              <input type="text" id="user-search" class="input-field" placeholder="Kullanıcı kodu veya e-posta girin...">
            </div>
            <div class="user-list" id="private-user-list">
              <div class="no-users">Kullanıcı aramak için yukarıdaki alanı kullanın.</div>
            </div>
          </div>
          ${canCreateGroup ? `
          <div id="group-chat-form" class="chat-form" style="display: none;">
            <div class="input-group">
              <label for="group-name" class="input-label">Grup Adı</label>
              <input type="text" id="group-name" class="input-field" placeholder="Grup adı girin...">
            </div>
            <div class="input-group">
              <label for="group-user-search" class="input-label">Kullanıcı Ekle</label>
              <input type="text" id="group-user-search" class="input-field" placeholder="Kullanıcı adı veya e-posta girin...">
            </div>
            <div class="user-list" id="group-user-list"></div>
            <div class="selected-users" id="selected-group-users">
              <div class="no-users">Henüz kullanıcı seçilmedi.</div>
            </div>
          </div>
          ` : ''}
          ${canCreateAnnouncement ? `
          <div id="announcement-chat-form" class="chat-form" style="display: none;">
            <div class="input-group">
              <label for="announcement-name" class="input-label">Duyuru Grubu Adı</label>
              <input type="text" id="announcement-name" class="input-field" value="Duyurular" placeholder="Grup adı girin...">
            </div>
            <div class="input-group">
              <label for="announcement-desc" class="input-label">Açıklama</label>
              <textarea id="announcement-desc" class="input-field" placeholder="Duyuru grubu açıklaması..." rows="3">Bu grup duyurular için kullanılmaktadır. Sadece yöneticiler mesaj gönderebilir.</textarea>
            </div>
          </div>
          ` : ''}
        </div>
        <div class="modal-footer">
          <button id="create-chat-btn" class="btn primary-btn">Sohbet Başlat</button>
          <button class="btn secondary-btn modal-close-btn">İptal</button>
        </div>
      </div>
    `;

    // Modal oluştur
    const modal = document.createElement('div');
    modal.className = 'modal';
    modal.innerHTML = modalHtml;
    document.body.appendChild(modal);

    // Modal kapatma işlevi
    const closeModal = () => {
      document.body.removeChild(modal);
    };

    // Kapatma düğmelerini dinle
    modal.querySelectorAll('.modal-close-btn').forEach(btn => {
      btn.addEventListener('click', closeModal);
    });

    // Dışa tıklama ile kapatma
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        closeModal();
      }
    });

    // Sohbet türü seçimini dinle
    const chatTypeBtns = modal.querySelectorAll('.chat-type-btn:not(.disabled)');
    const privateChatForm = modal.querySelector('#private-chat-form');
    const groupChatForm = modal.querySelector('#group-chat-form');
    const announcementChatForm = modal.querySelector('#announcement-chat-form');
    chatTypeBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        chatTypeBtns.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        if (btn.dataset.type === 'private') {
          privateChatForm.style.display = 'block';
          if (groupChatForm) groupChatForm.style.display = 'none';
          if (announcementChatForm) announcementChatForm.style.display = 'none';
        } else if (btn.dataset.type === 'group' && canCreateGroup) {
          privateChatForm.style.display = 'none';
          if (groupChatForm) groupChatForm.style.display = 'block';
          if (announcementChatForm) announcementChatForm.style.display = 'none';
        } else if (btn.dataset.type === 'announcement' && canCreateAnnouncement) {
          privateChatForm.style.display = 'none';
          if (groupChatForm) groupChatForm.style.display = 'none';
          if (announcementChatForm) announcementChatForm.style.display = 'block';
        }
      });
    });

    // Debounce fonksiyonu
    const debounce = (func, delay) => {
      let debounceTimer;
      return function(...args) {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => func.apply(this, args), delay);
      };
    };

    // Kullanıcı arama işlevi (Özel Sohbet)
    const userSearchInput = modal.querySelector('#user-search');
    const privateUserList = modal.querySelector('#private-user-list');
    userSearchInput.addEventListener('input', debounce(async (e) => {
      const searchValue = e.target.value.trim();
      if (searchValue.length < 2) {
        privateUserList.innerHTML = '<div class="no-users">En az 2 karakter girin.</div>';
        return;
      }
      try {
        const searchResults = await userService.findUsersByCodeOrEmail(searchValue);
        const filteredUsers = searchResults.filter(user => user.id !== this.userId);
        if (filteredUsers.length === 0) {
          privateUserList.innerHTML = '<div class="no-users">Kullanıcı bulunamadı.</div>';
          return;
        }
        privateUserList.innerHTML = '';
        filteredUsers.forEach(user => {
          const userEl = document.createElement('div');
          userEl.className = 'user-item';
          userEl.dataset.userId = user.id;
          let profilePic = user.photoURL;
          if (!profilePic) {
            profilePic = generateProfilePicture(user.userCode || user.displayName || user.email || 'Kullanıcı');
          }
          let displayName = '';
          if (user.userCode && user.userCode.trim() !== '') {
            displayName = user.userCode;
          } else if (user.displayName && user.displayName !== 'Kullanıcı') {
            displayName = user.displayName;
          } else {
            displayName = user.id.substring(0, 6);
          }
          const userRole = user.userRole || user.role || '';
          const roleDisplay = userRole ? `<div class="user-role">${userRole}</div>` : '';
          const emailDisplay = user.isEmailSearch && user.email ? `<div class="user-email">${user.email}</div>` : '';
          const showUserCode = user.userCode && user.userCode !== displayName;
          const codeDisplay = showUserCode ? `<div class="user-code">${user.userCode}</div>` : '';
          userEl.innerHTML = `
            <div class="user-avatar">
              <img src="${profilePic}" alt="${displayName}" />
            </div>
            <div class="user-info">
              <div class="user-name">${displayName}</div>
              ${roleDisplay}
              ${emailDisplay}
              ${codeDisplay}
            </div>
          `;
          userEl.addEventListener('click', () => {
            privateUserList.querySelectorAll('.user-item').forEach(item => {
              item.classList.remove('selected');
            });
            userEl.classList.add('selected');
          });
          privateUserList.appendChild(userEl);
        });
      } catch (error) {
        //console.error("Kullanıcı arama hatası:", error);
        privateUserList.innerHTML = '<div class="no-users">Arama sırasında bir hata oluştu.</div>';
      }
    }, 500));

    // Grup sohbeti için kullanıcı arama ve ekleme (sadece admin ve control için)
    if (canCreateGroup) {
      const groupUserSearchInput = modal.querySelector('#group-user-search');
      const groupUserList = modal.querySelector('#group-user-list');
      const selectedGroupUsers = modal.querySelector('#selected-group-users');
      groupUserSearchInput.addEventListener('input', debounce(async (e) => {
        const searchValue = e.target.value.trim();
        if (searchValue.length < 2) {
          groupUserList.innerHTML = '<div class="no-users">En az 2 karakter girin.</div>';
          return;
        }
        try {
          const searchResults = await userService.findUsersByCodeOrEmail(searchValue);
          const filteredUsers = searchResults.filter(user => user.id !== this.userId && !selectedGroupUserIds.has(user.id));
          if (filteredUsers.length === 0) {
            groupUserList.innerHTML = '<div class="no-users">Kullanıcı bulunamadı.</div>';
            return;
          }
          groupUserList.innerHTML = '';
          filteredUsers.forEach(user => {
            const userEl = document.createElement('div');
            userEl.className = 'user-item';
            userEl.dataset.userId = user.id;
            let profilePic = user.photoURL;
            if (!profilePic) {
              profilePic = generateProfilePicture(user.userCode || user.displayName || user.email || 'Kullanıcı');
            }
            let displayName = '';
            if (user.userCode && user.userCode.trim() !== '') {
              displayName = user.userCode;
            } else if (user.displayName && user.displayName !== 'Kullanıcı') {
              displayName = user.displayName;
            } else {
              displayName = user.id.substring(0, 6);
            }
            const userRole = user.userRole || user.role || '';
            const roleDisplay = userRole ? `<div class="user-role">${userRole}</div>` : '';
            const emailDisplay = user.isEmailSearch && user.email ? `<div class="user-email">${user.email}</div>` : '';
            const showUserCode = user.userCode && user.userCode !== displayName;
            const codeDisplay = showUserCode ? `<div class="user-code">${user.userCode}</div>` : '';
            userEl.innerHTML = `
              <div class="user-avatar">
                <img src="${profilePic}" alt="${displayName}" />
              </div>
              <div class="user-info">
                <div class="user-name">${displayName}</div>
                ${roleDisplay}
                ${emailDisplay}
                ${codeDisplay}
              </div>
              <div class="add-user-btn">+</div>
            `;
            userEl.addEventListener('click', () => {
              addUserToSelectedGroup(user);
              groupUserList.removeChild(userEl);
              groupUserSearchInput.value = '';
              if (groupUserList.children.length === 0) {
                groupUserList.innerHTML = '<div class="no-users">Kullanıcı aramak için yukarıdaki alanı kullanın.</div>';
              }
            });
            groupUserList.appendChild(userEl);
          });
        } catch (error) {
          //console.error("Kullanıcı arama hatası:", error);
          groupUserList.innerHTML = '<div class="no-users">Arama sırasında bir hata oluştu.</div>';
        }
      }, 500));
      const addUserToSelectedGroup = (user) => {
        if (selectedGroupUserIds.has(user.id)) return;
        selectedGroupUserIds.add(user.id);
        const noUsersEl = selectedGroupUsers.querySelector('.no-users');
        if (noUsersEl) {
          selectedGroupUsers.removeChild(noUsersEl);
        }
        const userEl = document.createElement('div');
        userEl.className = 'user-item selected';
        userEl.dataset.userId = user.id;
        let profilePic = user.photoURL;
        if (!profilePic) {
          profilePic = generateProfilePicture(user.userCode || user.displayName || user.email || 'Kullanıcı');
        }
        let displayName = '';
        if (user.userCode && user.userCode.trim() !== '') {
          displayName = user.userCode;
        } else if (user.displayName && user.displayName !== 'Kullanıcı') {
          displayName = user.displayName;
        } else {
          displayName = user.id.substring(0, 6);
        }
        const userRole = user.userRole || user.role || '';
        const roleDisplay = userRole ? `<span class="user-role-badge">${userRole}</span>` : '';
        userEl.innerHTML = `
          <div class="user-avatar">
            <img src="${profilePic}" alt="${displayName}" />
          </div>
          <div class="user-info">
            <div class="user-name">
              ${displayName} ${roleDisplay}
            </div>
          </div>
          <div class="remove-user-btn">×</div>
        `;
        const removeBtn = userEl.querySelector('.remove-user-btn');
        removeBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          selectedGroupUsers.removeChild(userEl);
          selectedGroupUserIds.delete(user.id);
          if (selectedGroupUserIds.size === 0) {
            selectedGroupUsers.innerHTML = '<div class="no-users">Henüz kullanıcı seçilmedi.</div>';
          }
        });
        selectedGroupUsers.appendChild(userEl);
      };
    }

    // Sohbet başlatma düğmesini dinle
    const createChatBtn = modal.querySelector('#create-chat-btn');
    createChatBtn.addEventListener('click', async () => {
      const activeBtn = modal.querySelector('.chat-type-btn.active');
      if (!activeBtn) return;
      const activeType = activeBtn.dataset.type;
      if (activeType === 'private') {
        const selectedUser = privateUserList.querySelector('.user-item.selected');
        if (selectedUser) {
          this.startPrivateChat(selectedUser.dataset.userId);
          closeModal();
        } else {
          alert('Lütfen bir kullanıcı seçin.');
        }
      } else if (activeType === 'group' && canCreateGroup) {
        const groupName = modal.querySelector('#group-name').value.trim();
        const selectedUsers = Array.from(selectedGroupUserIds);
        if (!groupName) {
          alert('Lütfen bir grup adı girin.');
          return;
        }
        if (selectedUsers.length === 0) {
          alert('Lütfen en az bir kullanıcı seçin.');
          return;
        }
        this.startGroupChat(groupName, [...selectedUsers, this.userId]);
        closeModal();
      } else if (activeType === 'announcement' && canCreateAnnouncement) {
        const groupName = modal.querySelector('#announcement-name').value.trim() || "Duyurular";
        const description = modal.querySelector('#announcement-desc').value.trim();
        try {
          const chatData = {
            users: [],
            isGroup: true,
            groupName: groupName,
            description: description,
            isAnnouncement: true,
            createdBy: this.userId,
            createdAt: new Date().toISOString(),
            writeAccess: ["admin", "control"]
          };
          const chatId = await chatService.createChat(chatData);
          closeModal();
          alert("Duyuru grubu başarıyla oluşturuldu!");
          if (chatId) {
            this.selectChat(chatId);
          }
        } catch (error) {
          //console.error("Duyuru grubu oluşturma hatası:", error);
          alert("Duyuru grubu oluşturulurken bir hata oluştu.");
        }
      }
    });
  }
  
  async startPrivateChat(otherUserId) {
    try {
      // GEÇİCİ: Duplicate temizleme devre dışı - sohbetler silinmesin!
      // await chatService.cleanupDuplicateChats();
      
      // chatService'in findOrCreatePrivateChat metodunu kullan
      const chatId = await chatService.findOrCreatePrivateChat(this.userId, otherUserId);
      this.selectChat(chatId);
    } catch (error) {
      //console.error("Özel sohbet başlatma hatası:", error);
      alert("Sohbet başlatılırken bir hata oluştu.");
    }
  }
  
  async startGroupChat(groupName, userIds) {
    try {
      // chatService ile grup sohbeti oluştur
      const chatData = {
        users: userIds,
        isGroup: true,
        groupName: groupName,
        createdBy: this.userId,
        createdAt: new Date().toISOString()
      };
      
      const chatId = await chatService.createChat(chatData);
      this.selectChat(chatId);
    } catch (error) {
      //console.error("Grup sohbeti başlatma hatası:", error);
      alert("Grup sohbeti başlatılırken bir hata oluştu.");
    }
  }
  
  ensureContextMenuStyles () {
    if (document.querySelector('style#context-menu-styles')) {
      return
    }
    const style = document.createElement('style')
    style.id = 'context-menu-styles'
    style.textContent = `
        .chat-context-menu {
          background: var(--bg-primary);
          border: 1px solid var(--bg-tertiary);
          border-radius: var(--radius-md);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          padding: var(--space-xs);
          z-index: 10000;
          min-width: 150px;
        }
        
        .context-menu-item {
          display: flex;
          align-items: center;
          gap: var(--space-xs);
          padding: var(--space-xs) var(--space-sm);
          border-radius: var(--radius-sm);
          cursor: pointer;
          color: var(--text-primary);
          font-size: var(--font-sm);
          transition: var(--transition-fast);
        }
        
        .context-menu-item:hover {
          background-color: var(--bg-hover);
        }

        .context-menu-item.danger {
          color: var(--text-danger, #cf6679);
        }
        
        .context-menu-item svg {
          flex-shrink: 0;
        }
      `
    document.head.appendChild(style)
  }

  showCloseGroupMenu (x, y, chatId, groupName) {
    const existingMenu = document.querySelector('.chat-context-menu')
    if (existingMenu) {
      existingMenu.remove()
    }
    this.ensureContextMenuStyles()
    const menu = document.createElement('div')
    menu.className = 'chat-context-menu'
    menu.innerHTML = `
      <div class="context-menu-item danger" data-action="close-group">
        Grubu tamamen kapat
      </div>
    `
    menu.style.position = 'fixed'
    menu.style.left = `${x}px`
    menu.style.top = `${y}px`
    document.body.appendChild(menu)
    const closeMenu = (event) => {
      if (!menu.contains(event.target)) {
        menu.remove()
        document.removeEventListener('click', closeMenu)
      }
    }
    setTimeout(() => {
      document.addEventListener('click', closeMenu)
    }, 0)
    menu.querySelector('[data-action="close-group"]').addEventListener('click', () => {
      menu.remove()
      document.removeEventListener('click', closeMenu)
      this.closeGroup(chatId, groupName)
    })
  }

  showContextMenu(x, y, chatId) {
    this.ensureContextMenuStyles()
    // Varolan context menu'yu kaldır
    const existingMenu = document.querySelector('.chat-context-menu');
    if (existingMenu) {
      existingMenu.remove();
    }
    
    // Yeni context menu oluştur
    const menu = document.createElement('div');
    menu.className = 'chat-context-menu';
    menu.innerHTML = `
      <div class="context-menu-item" data-action="archive">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="21 8 21 21 3 21 3 8"></polyline>
          <rect x="1" y="3" width="22" height="5"></rect>
          <line x1="10" y1="12" x2="14" y2="12"></line>
        </svg>
        Arşivle
      </div>
    `;
    
    // Menüyü konumlandır
    menu.style.position = 'fixed';
    menu.style.left = `${x}px`;
    menu.style.top = `${y}px`;
    
    // Body'e ekle
    document.body.appendChild(menu);
    
    // Menü dışına tıklandığında kapat
    const closeMenu = (e) => {
      if (!menu.contains(e.target)) {
        menu.remove();
        document.removeEventListener('click', closeMenu);
      }
    };
    
    // Bir sonraki tıklamada dinlemeye başla
    setTimeout(() => {
      document.addEventListener('click', closeMenu);
    }, 0);
    
    // Menü öğelerine tıklama event'leri
    const archiveBtn = menu.querySelector('[data-action="archive"]');
    archiveBtn.addEventListener('click', () => {
      this.archiveChat(chatId);
      menu.remove();
    });
  }

  showPurgeStatus (text) {
    let el = document.querySelector('.retention-purge-status')
    if (!el) {
      el = document.createElement('div')
      el.className = 'retention-purge-status'
      el.setAttribute('role', 'status')
      document.body.appendChild(el)
      if (!document.querySelector('style#retention-purge-styles')) {
        const style = document.createElement('style')
        style.id = 'retention-purge-styles'
        style.textContent = `
          .retention-purge-status {
            position: fixed;
            left: 16px;
            right: 16px;
            bottom: 16px;
            z-index: 10060;
            max-width: 480px;
            margin: 0 auto;
            padding: 12px 14px;
            border-radius: 10px;
            background: var(--bg-secondary, #2a2a2a);
            color: var(--text-primary, #fff);
            border: 1px solid var(--bg-tertiary, #444);
            box-shadow: 0 8px 24px rgba(0,0,0,0.35);
            font-size: 13px;
          }
        `
        document.head.appendChild(style)
      }
    }
    el.textContent = text
    return el
  }

  async closeGroup (chatId, groupName) {
    const promptInfo = describeGroupClose(groupName)
    if (!window.confirm(promptInfo.confirm)) {
      return
    }
    const typed = window.prompt(promptInfo.prompt)
    if (typed == null) {
      return
    }
    if (typed.trim() !== promptInfo.name) {
      alert('Grup adı eşleşmedi. Kapatma iptal edildi.')
      return
    }
    const status = this.showPurgeStatus('Grup kapatılıyor…')
    if (this.selectedChatId === chatId) {
      this.selectedChatId = null
      document.dispatchEvent(new CustomEvent('chat-archived', { detail: { chatId } }))
    }
    try {
      const result = await chatService.closeGroupCompletely(chatId, this.userId, (progress) => {
        status.textContent = `Grup kapatılıyor… ${progress.deletedDocs || 0} mesaj silindi.`
      })
      status.remove()
      alert(`«${result.groupName}» kapatıldı. ${result.deletedMessages} mesaj ve ${result.deletedFiles} dosya silindi.`)
    } catch (error) {
      status.remove()
      alert(error.message || 'Grup kapatılamadı.')
    }
  }

  async archiveChat(chatId) {
    // Onay sor
    const confirmed = confirm('Bu sohbeti arşivlemek istediğinizden emin misiniz? Sohbet ve tüm mesajlar arşive taşınacak.');
    if (!confirmed) return;
    
    try {
      // Arşivleme işlemi
      await chatService.archiveChat(chatId, this.userId);
      
      // Başarı mesajı
      alert('Sohbet başarıyla arşivlendi.');
      
      // Eğer arşivlenen sohbet seçili ise, seçimi kaldır
      if (this.selectedChatId === chatId) {
        this.selectedChatId = null;
        // Chat penceresini temizle
        const event = new CustomEvent('chat-archived', { detail: { chatId } });
        document.dispatchEvent(event);
      }
    } catch (error) {
      console.error('Sohbet arşivlenirken hata:', error);
      alert(error.message || 'Sohbet arşivlenirken bir hata oluştu.');
    }
  }
}

export function initChatList(userId) {
  const chatListContainer = document.getElementById('chat-list');
  if (chatListContainer && userId) {
    return new ChatList(chatListContainer, userId);
  }
  return null;
} 